/**
 * The visit timeline from the prenatal handbook, section 14
 * ("Your plan after baby arrives"), keyed to baby's day of life.
 */
import { dayOfLife } from './time';
import type { Profile } from './types';

export type VisitKey = 'newborn' | 'lactation' | 'twoWeek' | 'oneMonth';
export type VisitStatus = 'upcoming' | 'due' | 'scheduled' | 'past';

export interface VisitInfo {
  key: VisitKey;
  /** day-of-life window for the visit */
  windowStart: number;
  windowEnd: number;
  /** ISO date if the family marked it booked */
  scheduledFor?: string;
  status: VisitStatus;
  /** true when the "schedule now" prompt should be front and center */
  actionNeeded: boolean;
}

const WINDOWS: Record<VisitKey, [number, number]> = {
  newborn: [3, 5],
  lactation: [4, 7],
  twoWeek: [12, 16],
  oneMonth: [28, 35],
};

/** How many days before the window opens we start prompting to book. */
const PROMPT_LEAD_DAYS: Record<VisitKey, number> = {
  newborn: 3, // prompt from day 0 — booking this is the app's #1 job
  lactation: 2,
  twoWeek: 5,
  oneMonth: 10,
};

export function visitTimeline(profile: Profile, now: Date = new Date()): VisitInfo[] {
  const day = dayOfLife(profile.birthAt, now);
  if (day === null) return [];

  const scheduled: Partial<Record<VisitKey, string | undefined>> = {
    newborn: profile.newbornCheckupAt,
    lactation: profile.lactationFollowupAt,
  };

  return (Object.keys(WINDOWS) as VisitKey[]).map((key) => {
    const [windowStart, windowEnd] = WINDOWS[key];
    const scheduledFor = scheduled[key];
    let status: VisitStatus;
    if (scheduledFor) {
      // stays "scheduled" until well past the window
      status = day > windowEnd + 7 ? 'past' : 'scheduled';
    } else if (day > windowEnd) {
      status = 'past';
    } else if (day >= windowStart - PROMPT_LEAD_DAYS[key]) {
      status = 'due';
    } else {
      status = 'upcoming';
    }
    return {
      key,
      windowStart,
      windowEnd,
      scheduledFor,
      status,
      actionNeeded: status === 'due',
    };
  });
}

/** Days since birth we keep showing the congratulations card. */
export const CONGRATS_DAYS = 3;

export function showCongrats(profile: Profile, dismissed: boolean, now: Date = new Date()): boolean {
  if (dismissed || !profile.birthAt) return false;
  const day = dayOfLife(profile.birthAt, now);
  return day !== null && day <= CONGRATS_DAYS;
}
